import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Reklaim deltas vs the v4 shadcn registry (preserved on REK-1563 Stack 2 refresh):
 * - `h-11` (44px) instead of v4's `h-9` (36px) -- larger touch target sized for the
 *   form-heavy admin + auth + survey wizard surfaces. WCAG 2.5.5 prefers >= 44x44.
 * - `rounded-xl` instead of v4's `rounded-md` -- matches Reklaim's brand-shape language
 *   (pill-ish form fields, see auth + signup flows).
 * - `px-4 py-2` instead of v4's `px-3 py-1` -- matches the larger height.
 * - `bg-background` instead of v4's `bg-transparent` -- keeps the field visually anchored
 *   on hero / marketing backgrounds where transparent would disappear.
 * - `ring-2` instead of v4's `ring-1` focus state -- matches the dashboard's accent ring
 *   density. Combined with `ring-offset-2` it forms the Reklaim primary-yellow focus halo.
 *
 * Adopted from the v4 registry:
 * - (none yet; the v4 Input has no Portal / transform / a11y additions worth merging in).
 *
 * Do NOT replace this file with `npx shadcn add input --overwrite` -- it would shrink
 * every form field across the app + break visual parity with the brand language.
 */
const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex h-11 w-full rounded-xl border border-input bg-background px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export { Input };
